import { useState } from 'react'
import { assignTask, setTaskRepeat, setTaskReminders, updateTodo, type LetterTask } from '@core/letters'
import { TASK_CADENCES, describeRepeat } from '@core/taskRepeat'
import type { FamilyMember } from '@core/session'
import { Button, ErrorNote, Field } from '@design/components'
import { Overlay } from '@design/Overlay'

/**
 * One task, opened.
 *
 * Everything you can do to a task that is not "tick it off" lives here, which
 * is why the list itself stays a single circle per row. A list where three
 * things are tappable is a list you tap wrong, and the one tap that has to be
 * right is the one that finishes something.
 *
 * Correcting a typo matters more than it sounds. Before this, a task with the
 * wrong word in it could only be deleted and retyped — and a list you cannot
 * correct is a list you quietly stop trusting.
 *
 * Assignment is deliberately a separate save from the text. Handing something
 * to your mother sends her a notification; changing your own spelling should
 * not.
 */
export function TodoSheet({
  task,
  family,
  onClose,
  onChanged,
  onRemove,
}: {
  task: LetterTask
  family: FamilyMember[]
  onClose: () => void
  onChanged: () => void
  onRemove: () => Promise<void>
}) {
  const [title, setTitle] = useState(task.title)
  const [dueDate, setDueDate] = useState(task.dueDate ?? '')
  const [assignedTo, setAssignedTo] = useState<string | null>(task.assignedTo)
  const [note, setNote] = useState(task.assignmentNote ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [repeat, setRepeat] = useState<string | null>(task.repeatRule)
  const [remind, setRemind] = useState<number[]>(task.remindDaysBefore)

  const handOverChanged =
    assignedTo !== task.assignedTo || note.trim() !== (task.assignmentNote ?? '').trim()

  async function save() {
    setBusy(true)
    setError('')
    try {
      if (title.trim() !== task.title || (dueDate || null) !== task.dueDate) {
        await updateTodo(task.id, title, dueDate || null)
      }
      if (handOverChanged) {
        await assignTask(task.id, assignedTo, note)
      }
      if (repeat !== task.repeatRule) {
        await setTaskRepeat(task.id, repeat)
      }
      if (remind.join() !== task.remindDaysBefore.join()) {
        await setTaskReminders(task.id, remind)
      }
      onChanged()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan lukte niet.')
      setBusy(false)
    }
  }

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onClose} className="text-muted px-2 py-2">
          Sluiten
        </button>
        <span className="font-medium">Taak</span>
        <span className="w-16" />
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        <Field
          label="Wat moet er gebeuren?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <Field
          label="Wanneer? (mag leeg blijven)"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />

        {family.length > 0 && (
          <>
            <p className="text-sm text-muted mb-1.5">Wie doet het?</p>
            <div className="flex flex-wrap gap-2 mb-4">
              <Choice label="Ik" on={assignedTo === null} onPick={() => setAssignedTo(null)} />
              {family.map((member) => (
                <Choice
                  key={member.userId}
                  label={member.displayName}
                  on={assignedTo === member.userId}
                  onPick={() => setAssignedTo(member.userId)}
                />
              ))}
            </div>
          </>
        )}

        {/* Reminders need a date too, for the same reason. "A week before" is
            a week before *something*. */}
        {dueDate && (
          <>
            <p className="text-sm text-muted mb-1.5">Wanneer wil je gewaarschuwd worden?</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {LEAD_TIMES.map((lead) => {
                const on = remind.includes(lead.days)
                return (
                  <Choice
                    key={lead.days}
                    label={lead.label}
                    on={on}
                    onPick={() =>
                      setRemind((now) =>
                        on
                          ? now.filter((d) => d !== lead.days)
                          : [...now, lead.days].sort((a, b) => b - a),
                      )
                    }
                  />
                )
              })}
            </div>
            <p className="text-xs text-muted mb-4">
              {remind.length >= 3
                ? 'Drie is het maximum. Vaker vragen om aandacht helpt niet — dan hoort het een andere datum te krijgen.'
                : 'Op de dag zelf krijg je sowieso een bericht. Dit komt daarbovenop.'}
            </p>
          </>
        )}

        {/* Repeat needs a date to hang on: "every week" is meaningless without
            knowing which day, and RRULE builds the rule from the due date. So
            the control only appears once there is one — better than offering it
            and then explaining why it did nothing. */}
        {dueDate && (
          <>
            <p className="text-sm text-muted mb-1.5">Komt dit terug?</p>
            <div className="flex flex-wrap gap-2 mb-2">
              <Choice label="Nee" on={repeat === null} onPick={() => setRepeat(null)} />
              {TASK_CADENCES.map((c) => {
                const rule = c.build(new Date(dueDate + 'T12:00:00'))
                return (
                  <Choice
                    key={c.key}
                    label={c.label}
                    on={repeat === rule}
                    onPick={() => setRepeat(rule)}
                  />
                )
              })}
            </div>
            {repeat && (
              <p className="text-xs text-muted mb-4">
                Komt {describeRepeat(repeat)} terug. De volgende verschijnt zodra
                je deze afvinkt — niet eerder, zodat er nooit twee tegelijk in je
                lijst staan.
              </p>
            )}
          </>
        )}

        {assignedTo && (
          <Field
            label="Wat moeten ze precies doen?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Vraag naar de nieuwe polis"
            hint={
              assignedTo === task.assignedTo
                ? 'Dit is wat ze te zien krijgen.'
                : 'Ze krijgen dit als bericht, dus schrijf het zoals je het zou zeggen.'
            }
          />
        )}

        <ErrorNote>{error}</ErrorNote>

        {/* Two taps, on purpose. Deleting is the one thing here that cannot be
            undone from this screen — it can be undone from de Prullenbak, but
            only by someone who knows that. */}
        <div className="mt-8 pt-4 border-t border-line">
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              className="w-full py-3 text-sm text-muted"
            >
              Weggooien
            </button>
          ) : (
            <div className="text-center">
              <p className="text-sm text-muted mb-2">
                Weg uit de lijst. Je kan het 30 dagen terughalen uit de prullenbak.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  className="flex-1 min-h-[2.75rem] rounded-xl border border-line text-sm"
                >
                  Toch niet
                </button>
                <button
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await onRemove()
                      onClose()
                    } catch {
                      setError('Weggooien lukte niet.')
                      setBusy(false)
                    }
                  }}
                  className="flex-1 min-h-[2.75rem] rounded-xl border border-ink bg-ink/5 text-sm font-medium"
                >
                  Weggooien
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button size="large" onClick={save} disabled={busy}>
          {busy ? 'Bezig…' : 'Bewaren'}
        </Button>
      </div>
    </Overlay>
  )
}

function Choice({ label, on, onPick }: { label: string; on: boolean; onPick: () => void }) {
  return (
    <button
      onClick={onPick}
      className={`min-h-[2.75rem] px-4 rounded-xl border text-sm font-medium ${
        on ? 'border-ink bg-ink/5' : 'border-line text-muted'
      }`}
    >
      {label}
    </button>
  )
}

/**
 * The lead times a household actually uses.
 *
 * A fixed short list, not a number field. "How many days beforehand" is a
 * question nobody wants to answer with a keyboard, and the honest answers are
 * about five. Three may be picked at once; the database refuses a fourth.
 */
const LEAD_TIMES: { days: number; label: string }[] = [
  { days: 1, label: 'Dag ervoor' },
  { days: 2, label: '2 dagen' },
  { days: 7, label: 'Week ervoor' },
  { days: 14, label: '2 weken' },
  { days: 30, label: 'Maand ervoor' },
]
